import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { REOPEN_WAGE_BREAKDOWN_STATE_KEY, type WageBreakdownOrigin, type WageBreakdownReopen } from "../../utils/navigation/wageBreakdownReturn";

/**
 * DAT-906 · The way back to the wage breakdown a page was opened from: the day schedule as it was left
 * (same date, same Scheduled/Actual view), asked to open that shift's breakdown again. `undefined` when the
 * page was not opened from a breakdown — there is nowhere to go back to.
 */
export const useWageBreakdownReturn = (origin: WageBreakdownOrigin | undefined): (() => void) | undefined => {
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useMemo(() => {
		if (origin === undefined) return undefined;
		return () => {
			track("wage_breakdown_return_clicked", {
				department_id: departmentId,
				destination: origin.destination,
				shift_id: origin.shiftId,
				is_actual: origin.isActual,
			});
			const reopen: WageBreakdownReopen = { shiftId: origin.shiftId, isActual: origin.isActual, detailsOpen: origin.detailsOpen };
			navigate(origin.returnTo, { state: { [REOPEN_WAGE_BREAKDOWN_STATE_KEY]: reopen } });
		};
	}, [origin, departmentId, navigate, track]);
};
